/**
 * Web Audio로 소리를 내는 쪽. 못 내는 환경(옛 브라우저, 막힌 컨텍스트, 시험)에서는 조용히 아무것도 하지 않는다 —
 * **소리는 게임을 멈출 까닭이 되지 않는다.** 그래서 밖으로 나가는 함수는 모두 예외를 삼킨다.
 *
 * 줄기: 조리법(`synth.ts`) → 전장 줄/단추 줄 → 으뜸 음량 → 누르개 → 출력.
 * - 줄(전장·단추)은 갈아 끼울 수 있다. 판을 닫거나 되감으면 전장 줄을, 소리를 끄거나 컨텍스트가 서면 둘 다 떼어 내고
 *   새 줄을 단다(`swap`) — 이미 걸어 둔 북·징이 다음 화면이나 다시 켠 뒤에 이어 나지 않는다. 마디를 하나하나 세우는
 *   것보다 줄을 끊는 쪽이 빠짐이 없다.
 * - 누르개(`DynamicsCompressor`)는 여럿이 겹쳐 커질 때 깨지지 않게 하는 울타리다. 벽은 아니다 — 소리 하나의 크기는
 *   조리법에서 맞추고, 겹치는 수는 `AudioDirector`가 줄인다.
 *
 * **컨텍스트가 돌고 있을 때만 소리를 건다**(`play`). 처음에는 서 있는 컨텍스트에도 걸었다("깨어나면 난다"). 그런데 선
 * 동안에는 컨텍스트의 시계가 서 있어서, 브라우저가 깨우기를 거절하거나 OS가 오디오를 끊은 채로 판이 돌면 모든 소리가
 * 같은 시각에 쌓였다가 깨어나는 순간 한꺼번에 났다 — 12초 동안 쌓인 160개가 출력 1.63으로 터졌다(리뷰가 재현했다).
 *
 * 컨텍스트는 하나만, 처음 소리를 켠 손짓에서 만든다. 브라우저는 손짓 없이 만든 컨텍스트를 세워 둔다(자동 재생 정책).
 * `resume`은 받아 오기·풀기를 기다리지 않고 손짓 안에서 바로 청한다(사파리는 손짓에서 멀어지면 거절한다).
 */
import type { GrainReq } from './battle-mix.ts';
import type { CueId } from './cues.ts';
import type { AudioBackend } from './director.ts';
import { GRAINS, GRAIN_MAX_MS, makeBed, type Bed } from './grains.ts';
import { RECIPES } from './synth.ts';

type Ctor = typeof AudioContext;

function contextCtor(): Ctor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as { AudioContext?: Ctor; webkitAudioContext?: Ctor };
  return w.AudioContext ?? w.webkitAudioContext ?? null;
}

/** 끈 뒤 컨텍스트를 세우기까지(ms). 줄이 잦아들 틈이다 — 바로 세우면 울리던 파형이 그 자리에서 끊겨 딱 소리가 난다(QA). */
const SUSPEND_AFTER_MS = 80;
/** 깔개의 크기를 이만큼 새로 듣지 못하면 내린다(ms). 틱이 돌지 않는다는 뜻이다 — 판을 세웠거나(부대 고르기), 다시 보기를
 *  멈췄거나, 판이 끝났다. 선 판에서 아우성만 이어지면 안 된다. */
const BED_IDLE_MS = 400;
/** 내린 깔개를 이만큼 뒤에 아예 놓는다(ms). 내리기만 하면 소리 없는 잡음 고리가 다음 판까지 돈다 — 결과·편성 화면
 *  내내 폰이 그것을 돌린다(리뷰·QA). 잦아들 틈(시간 상수 0.25초)은 준다. */
const BED_DROP_MS = 1200;

export class WebAudioBackend implements AudioBackend {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private ui: GainNode | null = null;
  private battle: GainNode | null = null;
  private gain = 0;
  private hidden = false;
  /** 만들다 실패했다 — 손짓마다 다시 만들려 들지 않는다. */
  private broken = false;
  /** 끄거나 가려지거나 컨텍스트가 설 때마다 오른다. 그 전에 맡긴 일(`whenRunning`)은 버린다. */
  private gen = 0;
  /** 장치를 놓으려고 걸어 둔 타이머(`rest`). */
  private restTimer: ReturnType<typeof setTimeout> | null = null;
  /** 전장의 깔개. 전장 줄에 달려 있어 줄을 갈면 함께 떨어진다. 처음 크기가 생길 때 만든다. */
  private bedNode: Bed | null = null;
  private bedTimer: ReturnType<typeof setTimeout> | null = null;
  /** 깔개를 이미 내렸고 놓을 때를 기다리는 중이다. 그동안의 `bed(0)`은 그 때를 미루지 않는다. */
  private bedDown = false;

  /**
   * 손짓(누름) 안에서 부른다: 컨텍스트가 없으면 만들고, 서 있으면 깨운다. 음량이 0이면 아무것도 만들지 않는다 —
   * 소리를 켠 적 없는 사람의 폰에서 오디오 장치를 붙들지 않는다.
   */
  unlock(): void {
    if (this.gain <= 0 || this.broken) return;
    try {
      if (!this.ctx) {
        const C = contextCtor();
        if (!C) {
          this.broken = true;
          return;
        }
        const ctx = new C({ latencyHint: 'interactive' });
        const comp = ctx.createDynamicsCompressor();
        comp.threshold.value = -12;
        comp.knee.value = 10;
        comp.ratio.value = 8;
        comp.attack.value = 0.003;
        comp.release.value = 0.2;
        const master = ctx.createGain();
        master.gain.value = this.gain;
        master.connect(comp).connect(ctx.destination);
        this.ctx = ctx;
        this.master = master;
        this.ui = this.newBus();
        this.battle = this.newBus();
        // 우리가 세우지 않아도 선다(전화, 다른 앱, 사파리의 `interrupted`). 그때 걸려 있던 소리는 버린다.
        ctx.onstatechange = () => {
          if (ctx.state !== 'running') this.drop();
        };
      }
      if (this.ctx.state !== 'running') void this.ctx.resume().catch(() => {});
    } catch {
      this.broken = true;
      this.ctx = null;
    }
  }

  /**
   * 컨텍스트가 돌고 있으면 바로, 아니면 깨어난 뒤에 `fn`을 부른다 — 소리를 켠 그 누름의 확인음 하나를 위한 길이다
   * (방금 만든 컨텍스트는 `resume`이 끝나기 전일 수 있다). `withinMs`를 넘겼거나 그 사이 끄거나 가려졌으면 버린다.
   */
  whenRunning(fn: () => void, withinMs = 600): void {
    const ctx = this.ctx;
    if (!ctx || this.gain <= 0) return;
    if (ctx.state === 'running') {
      fn();
      return;
    }
    const gen = this.gen;
    const t0 = performance.now();
    try {
      void ctx
        .resume()
        .then(() => {
          if (gen === this.gen && ctx.state === 'running' && performance.now() - t0 <= withinMs) fn();
        })
        .catch(() => {});
    } catch {
      // 못 깨워도 게임은 돈다.
    }
  }

  /** 으뜸 음량(0이면 끔). 바꿀 때 딱 소리가 나지 않게 짧게 옮긴다. */
  setGain(gain: number): void {
    const was = this.gain;
    this.gain = gain;
    if (!this.ctx || !this.master) return;
    try {
      if (gain <= 0) {
        if (was > 0) this.rest();
        return;
      }
      const t = this.ctx.currentTime;
      this.master.gain.cancelScheduledValues(t);
      this.master.gain.setTargetAtTime(gain, t, 0.02);
    } catch {
      // 못 바꿔도 게임은 돈다.
    }
  }

  /** 탭이 가려졌다/돌아왔다. 가려지면 걸려 있던 소리를 버린다 — 돌아왔을 때 한참 전의 북이 울리지 않게. */
  setHidden(hidden: boolean): void {
    const was = this.hidden;
    this.hidden = hidden;
    if (!this.ctx) return;
    try {
      if (hidden) {
        if (!was) this.rest();
      } else if (this.gain > 0) {
        // 손짓 없이 깨우는 것이 거절될 수 있다(사파리). 그때는 다음 누름의 `unlock`이 깨운다.
        void this.ctx.resume().catch(() => {});
      }
    } catch {
      // 위와 같다.
    }
  }

  /** 소리를 건다. 걸었으면 true — 컨텍스트가 돌고 있을 때만 건다(머리말). */
  play(cue: CueId): boolean {
    const ctx = this.ctx;
    if (!ctx || ctx.state !== 'running' || this.gain <= 0 || this.hidden) return false;
    const out = cue.startsWith('ui.') ? this.ui : this.battle;
    if (!out) return false;
    try {
      // 조금 뒤로 잡는다 — 지금 시각에 딱 맞추면 앞머리가 잘려 딱 소리가 날 수 있다.
      RECIPES[cue](ctx, out, ctx.currentTime + 0.015);
      return true;
    } catch {
      // 소리 하나를 못 내도 다음 소리는 낸다.
      return false;
    }
  }

  grain(req: GrainReq): void {
    const ctx = this.ctx;
    const bus = this.battle;
    if (!ctx || !bus || ctx.state !== 'running' || this.gain <= 0 || this.hidden) return;
    try {
      let out: AudioNode = bus;
      // 좌우: 알갱이마다 마디 하나를 달았다가 소리가 끝나면 뗀다. 옛 사파리에는 이 마디가 없다 — 가운데로 낸다.
      if (req.pan !== 0 && typeof ctx.createStereoPanner === 'function') {
        const pan = ctx.createStereoPanner();
        pan.pan.value = req.pan;
        pan.connect(bus);
        out = pan;
        setTimeout(() => {
          try {
            pan.disconnect();
          } catch {
            // 줄이 먼저 갈렸다.
          }
        }, req.delayMs + GRAIN_MAX_MS);
      }
      GRAINS[req.kind](ctx, out, ctx.currentTime + 0.015 + req.delayMs / 1000, req.gain, req.pitch);
    } catch {
      // 알갱이 하나를 못 내도 다음 것은 낸다.
    }
  }

  bed(level: number): void {
    const ctx = this.ctx;
    // 내린 채 기다리는 동안 0을 또 들으면 그대로 둔다. 다시 보기의 4배·8배는 틱마다 0을 보내는데, 그때마다 놓을 때를
    // 새로 잡았더니 소리 없는 깔개가 고배속 내내 돌았다(재리뷰).
    if (level <= 0 && this.bedDown && this.bedNode) return;
    this.bedDown = false;
    if (this.bedTimer !== null) {
      clearTimeout(this.bedTimer);
      this.bedTimer = null;
    }
    if (!ctx || !this.battle || ctx.state !== 'running' || this.gain <= 0 || this.hidden) return;
    try {
      if (!this.bedNode) {
        if (level <= 0.01) return;
        this.bedNode = makeBed(ctx, this.battle);
      }
      const node = this.bedNode;
      node.level(level);
      this.bedDown = level <= 0;
      // 크기가 있으면: 한동안 새로 듣지 못할 때 내린다(틱이 멎었다). 0이면: 잦아든 뒤에 놓는다. 그 사이 크기가 다시
      // 오면 위에서 타이머를 걷었으므로 이어진다.
      this.bedTimer = setTimeout(
        () => {
          this.bedTimer = null;
          if (this.bedNode !== node) return;
          if (level > 0) this.bed(0);
          else this.dropBed();
        },
        level > 0 ? BED_IDLE_MS : BED_DROP_MS,
      );
    } catch {
      // 깔개가 없어도 판은 돈다.
    }
  }

  stopBattle(): void {
    if (!this.ctx || !this.battle) return;
    try {
      this.dropBed();
      this.swap(this.battle);
      this.battle = this.newBus();
    } catch {
      // 위와 같다.
    }
  }

  /** 끄거나 가려졌다: 걸려 있던 소리를 버리고, 줄이 잦아든 뒤 장치를 놓는다. */
  private rest(): void {
    this.drop();
    const ctx = this.ctx;
    if (!ctx) return;
    // 앞의 타이머는 걷는다. 그대로 두면 끔 → 켬 → 다시 끔에서 첫 끔의 타이머가 마지막 끔의 1ms 뒤에 세워, 잦아들
    // 틈이 사라졌다(재리뷰).
    if (this.restTimer !== null) clearTimeout(this.restTimer);
    this.restTimer = setTimeout(() => {
      this.restTimer = null;
      // 그 사이 다시 켰거나 돌아왔으면 세우지 않는다.
      if (this.gain > 0 && !this.hidden) return;
      void ctx.suspend().catch(() => {});
    }, SUSPEND_AFTER_MS);
  }

  /** 깔개를 멈추고 놓는다. 다음에 크기가 생기면 새 줄에 다시 만든다. */
  private dropBed(): void {
    if (this.bedTimer !== null) {
      clearTimeout(this.bedTimer);
      this.bedTimer = null;
    }
    const b = this.bedNode;
    this.bedNode = null;
    this.bedDown = false;
    b?.stop();
  }

  /** 걸려 있던 소리를 모두 버린다(두 줄을 갈아 끼운다). 맡아 둔 일도 버린다. */
  private drop(): void {
    this.gen++;
    try {
      this.dropBed();
      if (this.ui) this.swap(this.ui);
      if (this.battle) this.swap(this.battle);
      this.ui = this.newBus();
      this.battle = this.newBus();
    } catch {
      // 위와 같다.
    }
  }

  /** 옛 줄을 떼어 낸다. 도는 중이면 짧게 줄인 뒤에 — 바로 떼면 파형이 끊겨 딱 소리가 난다. 서 있으면 시계가 서 있어 줄일 수 없으니 바로 뗀다. */
  private swap(old: GainNode): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const off = (): void => {
      try {
        old.disconnect();
      } catch {
        // 이미 떨어졌다.
      }
    };
    if (ctx.state !== 'running') {
      off();
      return;
    }
    const t = ctx.currentTime;
    old.gain.cancelScheduledValues(t);
    old.gain.setTargetAtTime(0, t, 0.012);
    setTimeout(off, 200);
  }

  private newBus(): GainNode | null {
    if (!this.ctx || !this.master) return null;
    const g = this.ctx.createGain();
    g.connect(this.master);
    return g;
  }
}
